import {createAsyncThunk, createSlice, PayloadAction, SerializedError} from '@reduxjs/toolkit'
import {Auth} from 'aws-amplify';
import {RootState} from '../../store/store';
import {Loading} from '../../App/app';
import {feedback} from '../../components/Feedback/feedback';
import axios from 'axios';
import {push} from '@lagunovsky/redux-react-router';
import {macs365Uri} from '../../uris';

export interface State {
  email: string,
  emailVerified: boolean,
  loading: Loading,
  modified: boolean,
  currentRequestId: string,
  error: SerializedError | null,
}

const initialState: State = {
  email: '',
  emailVerified: false,
  loading: 'idle',
  modified: false,
  currentRequestId: '',
  error: null,
}

interface EmailPayload {
  email?: string,
  emailVerified?: boolean,
  modified?: boolean,
}

export const verifyWithToken = createAsyncThunk(
  'email/verifyToken',
  async (token: string, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const {currentRequestId, loading} = state.pages.verify;

    if (loading !== 'pending' || thunkAPI.requestId !== currentRequestId) return

    return axios.post('/verifyCode', {
      token: token
    }).then(res => {
        window.location.replace(`${macs365Uri}/sign-out?reason=verification`)
        return true;
      })
      .catch(error => {
        // TODO: Check if axios has a error.response property
        const params = new URLSearchParams();
        params.set('message', error && typeof error === 'string' ? error : '');
        thunkAPI.dispatch(push(`/settings/verify/error?${params}`))
        return false;
      })
  }
)

interface Attributes {
  email: string
}

export const resent = createAsyncThunk(
  'resent',
  async (attributes: Attributes, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const {currentRequestId, loading} = state.pages.verify;

    if (loading !== 'pending' || thunkAPI.requestId !== currentRequestId) return;

    return Auth.currentAuthenticatedUser().then(cognitoUser => {
      const clientMetaData = {
        clientMetaEmail: attributes.email,
        redirectURI: state.app.searchParameters.redirect_uri
      }
      return Auth.verifyUserAttribute(cognitoUser, 'email', clientMetaData).then(() => {
        thunkAPI.dispatch(feedback({message: 'Email has been sent.'}))
        return true;
      })

    }).catch(error => {
      const message = typeof error === 'string' ? error : error.message;
      thunkAPI.dispatch(feedback({message: message, severity: 'error'}))
      return false;
    })
  }
)

export const verifyEmailSlice = createSlice({
  name: 'verifyEmail',
  initialState,
  reducers: {
    emailUpdated: (state, action: PayloadAction<EmailPayload>) => {
      state.email = action.payload.email ? action.payload.email : state.email;
      state.modified = action.payload.modified === false ? false : true;
      state.emailVerified = action.payload.emailVerified === false ? false : true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(verifyWithToken.pending, (state, action) => {
        if (state.loading === 'idle') {
          state.loading = 'pending'
          state.currentRequestId = action.meta.requestId
        }
      })
      .addCase(verifyWithToken.fulfilled, (state, action) => {
        const {requestId} = action.meta
        if (state.loading === 'pending' && state.currentRequestId === requestId) {

          // keep on success since user has to logout to refresh
          // authentication anyways

          state.loading = action.payload ? 'succeeded' : 'idle';
          state.currentRequestId = ''
        }
      })
      .addCase(verifyWithToken.rejected, (state, action,) => {

        const {requestId} = action.meta

        if (state.loading === 'pending' && state.currentRequestId === requestId) {
          state.loading = 'idle'
          state.error = action.error
          state.currentRequestId = ''
        }
      })
      .addCase(resent.pending, (state, action) => {
        if (state.loading === 'idle') {
          state.loading = 'pending'
          state.currentRequestId = action.meta.requestId
        }
      })
      .addCase(resent.fulfilled, (state, action) => {
        const {requestId} = action.meta
        if (state.loading === 'pending' && state.currentRequestId === requestId) {
          state.loading = 'idle'
          state.currentRequestId = ''
        }
      })
      .addCase(resent.rejected, (state, action,) => {
        const {requestId} = action.meta
        if (
          state.loading === 'pending' &&
          state.currentRequestId === requestId
        ) {
          state.loading = 'idle'
          state.error = action.error
          state.currentRequestId = ''
        }
      })
  }
})

export const {emailUpdated,} = verifyEmailSlice.actions

export default verifyEmailSlice.reducer
